import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { RoleProvider } from "@/context/RoleContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { moduleForPath } from "@/lib/roleMenus";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import WhatsAppActivation from "@/pages/WhatsAppActivation";
import BlockedAccount from "@/pages/BlockedAccount";
import SelectRole from "@/pages/SelectRole";
import PostRegister from "@/pages/PostRegister";

import Sobre from "@/pages/Sobre";
import Diagnosticos from "@/pages/Diagnosticos";
import Acompanamiento from "@/pages/Acompanamiento";
import AcademiaPublic from "@/pages/AcademiaPublic";
import Marketplace from "@/pages/Marketplace";
import Contacto from "@/pages/Contacto";

import { AppShellSwitch, MobileEntry, MobileDashboardRouter, MobileExplore } from "@/mobile/MobileExperience";
import MobileLogin from "@/mobile/MobileLogin";
import AppDownload, { LegacyInstalledAppRedirect } from "@/mobile/AppDownload";
import AuthGuard, { PermissionGuard } from "@/components/AuthGuard";
import PortalArrival from "@/components/PortalArrival";
import Estudios from "@/pages/dashboard/Estudios";
import Academia from "@/pages/dashboard/Academia";
import AcademyProgress from "@/pages/dashboard/AcademyProgress";
import AcademyCatalogAdmin from "@/pages/dashboard/AcademyCatalogAdmin";
import AcademyMaterials from "@/pages/dashboard/AcademyMaterials";
import AcademyExams from "@/pages/dashboard/AcademyExams";
import AcademyCertificates from "@/pages/dashboard/AcademyCertificates";
import Tienda from "@/pages/dashboard/Tienda";
import MarketplaceOrders from "@/pages/dashboard/MarketplaceOrders";
import CreatorProducts from "@/pages/dashboard/CreatorProducts";
import CreatorInventory from "@/pages/dashboard/CreatorInventory";
import CreatorMarketplaceOps from "@/pages/dashboard/CreatorMarketplaceOps";
import CreatorStore from "@/pages/dashboard/CreatorStore";
import CreatorAcademy from "@/pages/dashboard/CreatorAcademy";
import AcademyCreatorReview from "@/pages/dashboard/AcademyCreatorReview";
import MarketplaceModeration from "@/pages/dashboard/MarketplaceModeration";
import GlobalConfig from "@/pages/dashboard/GlobalConfig";
import CostsKD9 from "@/pages/dashboard/CostsKD9";
import LandingControl from "@/pages/dashboard/LandingControl";
import HoroscopeManager from "@/pages/dashboard/HoroscopeManager";
import HoroscopoDiario, { HoroscopeRoleGuard } from "@/pages/dashboard/HoroscopoDiario";
import NumerologiaViva from "@/pages/dashboard/NumerologiaViva";
import NumerologiaDirector from "@/pages/dashboard/NumerologiaDirector";
import WhatsAppCenter from "@/pages/dashboard/WhatsAppCenter";
import PaymentSettings from "@/pages/dashboard/PaymentSettings";
import AuditLogs from "@/pages/dashboard/AuditLogs";
import GlobalReports from "@/pages/dashboard/GlobalReports";
import MisPedidos from "@/pages/dashboard/MisPedidos";
import Perfil from "@/pages/dashboard/Perfil";
import Mensajes from "@/pages/dashboard/Mensajes";
import Consultor from "@/pages/dashboard/Consultor";
import Admin from "@/pages/dashboard/Admin";
import Creador from "@/pages/dashboard/Creador";
import Comunidad from "@/pages/dashboard/Comunidad";
import Favoritos from "@/pages/dashboard/Favoritos";
import AcompanamientoDashboard from "@/pages/dashboard/AcompanamientoDashboard";
import { ConsultantTalk, QuickConsultation, ConsultantServiceWork, QuickServiceDirector } from "@/pages/dashboard/TalkServices";
import AccompanimentManager from "@/pages/dashboard/AccompanimentManager";
import MiAgenda from "@/pages/dashboard/MiAgenda";
import GestionUsuarios from "@/pages/dashboard/GestionUsuarios";
import ActivityCenter, { SessionDiagnostics } from "@/pages/dashboard/ActivityCenter";
import RetentionRadar from "@/pages/dashboard/RetentionRadar";
import SoporteUser from "@/pages/dashboard/Soporte";
import SolicitudesRoles from "@/pages/dashboard/SolicitudesRoles";
import DiagnosticsCatalog from "@/pages/dashboard/DiagnosticsCatalog";
import MisSolicitudes from "@/pages/dashboard/MisSolicitudes";
import AdminDashboard from "@/pages/dashboard/AdminDashboard";
import DirectorDashboard from "@/pages/dashboard/DirectorDashboard";
import SupportCenter from "@/pages/dashboard/SupportCenter";
import ReportsAnalytics from "@/pages/dashboard/ReportsAnalytics";
import ConsultantWork from "@/pages/dashboard/ConsultantWork";
import { MyFinance, WithdrawalManagement } from "@/pages/dashboard/Finance";
import ConsultantEarnings from "@/pages/dashboard/ConsultantEarnings";
import EnergyKD from "@/pages/dashboard/EnergyKD";
import ThirdPartyPayments, { ThirdPartyPaymentsGovernance } from "@/pages/dashboard/ThirdPartyPayments";
import TemploVivo from "@/pages/dashboard/TemploVivo";
import TeamManagement from "@/pages/dashboard/TeamManagement";
import { OracleUser, OracleConsultant, OracleDirector } from "@/pages/dashboard/Oracle";
import TrustedTeamManagement from "@/pages/dashboard/TrustedTeamManagement";
import AdminDiagnosticOperations from "@/pages/dashboard/AdminDiagnosticOperations";
import {
  BolsaConsultas, Asignaciones, RevisionDiagnosticos,
  Pagos, Premium, SubirProducto,
} from "@/pages/dashboard/RolePages";
import {
  GraduationCap, TrendingUp, FileText, Layers, Briefcase, Send,
  BookOpen, ClipboardList, BarChart3, DollarSign, ShoppingBag,
  Calendar, Heart, Users, Crown, Settings, AlertTriangle, Award,
  ShieldCheck, Sparkles,
} from "lucide-react";

const AccountBlockRedirect = () => {
  const { blocked } = useAuth();
  const location = useLocation();
  if (blocked && location.pathname !== "/cuenta-bloqueada") {
    return <Navigate to="/cuenta-bloqueada" replace />;
  }
  return null;
};


const ModuleStateRedirect = () => {
  const { accessMap } = useAuth();
  const location = useLocation();
  if (!location.pathname.startsWith("/app/") || !accessMap) return null;
  if ((accessMap.roles || []).includes("director")) return null;
  const moduleKey = moduleForPath(location.pathname);
  if (moduleKey && accessMap.modules?.[moduleKey] === false) {
    return <Navigate to="/app/inicio" replace state={{ moduleDisabled: moduleKey }} />;
  }
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <RoleProvider>
          <AuthProvider>
            <div className="App">
              <Toaster
                position="top-right"
                theme="dark"
                richColors
                closeButton
                duration={2200}
              />
              <BrowserRouter>
                <AccountBlockRedirect />
                <ModuleStateRedirect />
                <PortalArrival />
                <LegacyInstalledAppRedirect />
                <Routes>
                {/* Public */}
                <Route path="/" element={<Landing />} />
                <Route path="/movil" element={<MobileEntry />} />
                <Route path="/movil/login" element={<MobileLogin />} />
                <Route path="/descarga" element={<AppDownload />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/verificar-whatsapp" element={<WhatsAppActivation />} />
                <Route path="/cuenta-bloqueada" element={<BlockedAccount />} />
                <Route path="/select-role" element={<SelectRole />} />
                <Route path="/post-register/:role" element={<PostRegister />} />

                {/* Public institutional */}
                <Route path="/sobre" element={<Sobre />} />
                <Route path="/diagnosticos" element={<Diagnosticos />} />
                <Route path="/estudios" element={<Diagnosticos />} />
                <Route path="/acompanamiento" element={<Acompanamiento />} />
                <Route path="/academia" element={<AcademiaPublic />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/tienda" element={<Marketplace />} />
                <Route path="/contacto" element={<Contacto />} />

                {/* App (dashboard) */}
                <Route
                  path="/app"
                  element={
                    <AuthGuard>
                      <AppShellSwitch />
                    </AuthGuard>
                  }
                >
                  <Route index element={<Navigate to="/app/inicio" replace />} />
                  <Route path="inicio" element={<MobileDashboardRouter />} />
                  <Route path="explorar-movil" element={<MobileExplore />} />

                  {/* User */}
                  <Route path="diagnosticos" element={<PermissionGuard allOf={["diagnostics.buy"]}><Estudios /></PermissionGuard>} />
                  <Route path="mis-diagnosticos" element={<PermissionGuard allOf={["diagnostics.buy"]}><MisPedidos /></PermissionGuard>} />
                  <Route path="mis-cursos" element={<PermissionGuard allOf={["academy.view"]}><AcademyProgress /></PermissionGuard>} />
                  <Route path="mis-estudios" element={<Navigate to="/app/mis-cursos" replace />} />
                  <Route path="mis-compras-marketplace" element={<PermissionGuard allOf={["marketplace.buy"]}><MarketplaceOrders /></PermissionGuard>} />
                  <Route path="mis-pagos" element={<Navigate to="/app/energia-kd" replace />} />
                  <Route path="energia-kd" element={<EnergyKD />} />
                  <Route path="pagos-terceros" element={<PermissionGuard allOf={["onepay.third_party.use"]}><ThirdPartyPayments /></PermissionGuard>} />
                  <Route path="templo-vivo" element={<TemploVivo />} />
                  <Route path="oraculo" element={<OracleUser />} />
                  <Route path="numerologia" element={<NumerologiaViva />} />
                  <Route path="horoscopo" element={<HoroscopeRoleGuard><HoroscopoDiario /></HoroscopeRoleGuard>} />
                  <Route path="agenda" element={<PermissionGuard anyOf={["agenda.view", "agenda.manage"]}><MiAgenda /></PermissionGuard>} />
                  <Route path="acompanamiento" element={<PermissionGuard allOf={["accompaniment.request"]}><AcompanamientoDashboard mode="accompaniment" /></PermissionGuard>} />
                  <Route path="videollamadas-kd9" element={<PermissionGuard allOf={["accompaniment.request"]}><AcompanamientoDashboard mode="video" /></PermissionGuard>} />
                  <Route path="hablar-consultor" element={<PermissionGuard allOf={["accompaniment.request"]}><ConsultantTalk /></PermissionGuard>} />
                  <Route path="consulta-rapida" element={<PermissionGuard allOf={["accompaniment.request"]}><QuickConsultation /></PermissionGuard>} />
                  <Route path="acompanamientos-director" element={<PermissionGuard allOf={["system.config"]}><AccompanimentManager /></PermissionGuard>} />
                  <Route path="soporte" element={<PermissionGuard anyOf={["support.create_ticket", "tickets.view"]}><SoporteUser /></PermissionGuard>} />
                  <Route path="comprobante" element={<Navigate to="/app/energia-kd" replace />} />
                  <Route path="mis-solicitudes" element={<MisSolicitudes />} />
                  <Route path="solicitudes-roles" element={<PermissionGuard anyOf={["consultant.requests.review", "creator.requests.review"]}><SolicitudesRoles /></PermissionGuard>} />

                  {/* Director KD9 (SuperAdmin) exclusive */}
                  <Route path="configuracion-global" element={<PermissionGuard allOf={["system.config"]}><GlobalConfig /></PermissionGuard>} />
                  <Route path="costos-kd9" element={<PermissionGuard allOf={["system.config"]}><CostsKD9 /></PermissionGuard>} />
                  <Route path="control-landing" element={<PermissionGuard allOf={["system.config"]}><LandingControl /></PermissionGuard>} />
                  <Route path="landing-vivo" element={<PermissionGuard allOf={["system.config"]}><HoroscopeManager /></PermissionGuard>} />
                  <Route path="whatsapp-notificaciones" element={<WhatsAppCenter />} />
                  <Route path="configuracion-pagos" element={<PermissionGuard allOf={["payments.settings.manage"]}><PaymentSettings /></PermissionGuard>} />
                  <Route path="configuracion-consulta-rapida" element={<PermissionGuard allOf={["system.config"]}><QuickServiceDirector /></PermissionGuard>} />
                  <Route path="gestion-retiros" element={<PermissionGuard allOf={["system.config"]}><WithdrawalManagement /></PermissionGuard>} />
                  <Route path="gobierno-pagos-terceros" element={<PermissionGuard allOf={["onepay.third_party.manage"]}><ThirdPartyPaymentsGovernance /></PermissionGuard>} />
                  <Route path="configuracion-ganancias-consultor" element={<PermissionGuard allOf={["system.config"]}><ConsultantEarnings director /></PermissionGuard>} />
                  <Route path="energia-kd-director" element={<PermissionGuard allOf={["system.config"]}><EnergyKD /></PermissionGuard>} />
                  <Route path="templo-vivo-director" element={<PermissionGuard allOf={["system.config"]}><TemploVivo /></PermissionGuard>} />
                  <Route path="oraculo-director" element={<PermissionGuard allOf={["system.config"]}><OracleDirector /></PermissionGuard>} />
                  <Route path="numerologia-director" element={<PermissionGuard allOf={["system.config"]}><NumerologiaDirector /></PermissionGuard>} />
                  <Route path="auditoria" element={<PermissionGuard allOf={["audit.view"]}><AuditLogs /></PermissionGuard>} />
                  <Route path="analitica-global" element={<PermissionGuard allOf={["audit.view"]}><GlobalReports /></PermissionGuard>} />
                  <Route path="consultores" element={<PermissionGuard allOf={["users.manage", "consultant.capabilities.manage"]}><TeamManagement role="consultant" title="CONSULTORES" /></PermissionGuard>} />
                  <Route path="creadores" element={<PermissionGuard allOf={["users.manage"]}><TeamManagement role="creator" title="CREADORES" /></PermissionGuard>} />

                  {/* Estudiante */}
                  <Route path="progreso" element={<PermissionGuard allOf={["academy.view"]}><AcademyProgress /></PermissionGuard>} />
                  <Route path="examenes" element={<PermissionGuard allOf={["exams.take"]}><AcademyExams /></PermissionGuard>} />
                  <Route path="materiales" element={<PermissionGuard allOf={["academy.view"]}><AcademyMaterials /></PermissionGuard>} />
                  <Route path="certificados" element={<PermissionGuard allOf={["academy.view"]}><AcademyCertificates /></PermissionGuard>} />

                  {/* Consultor */}
                  <Route path="bolsa-consultas" element={<PermissionGuard anyOf={["consultant.accompaniment.provide", "consultant.video_calls.provide"]}><BolsaConsultas /></PermissionGuard>} />
                  <Route path="bolsa-diagnosticos" element={<PermissionGuard allOf={["consultant.diagnostics.provide"]}><ConsultantWork pool /></PermissionGuard>} />
                  <Route path="mis-trabajos" element={<PermissionGuard allOf={["consultant.diagnostics.provide"]}><ConsultantWork /></PermissionGuard>} />
                  <Route path="informes" element={<PermissionGuard allOf={["consultant.diagnostics.provide"]}><ConsultantWork history /></PermissionGuard>} />
                  <Route path="finanzas" element={<PermissionGuard anyOf={["diagnostics.work", "marketplace.earnings.view_own"]}><MyFinance /></PermissionGuard>} />
                  <Route path="mis-ganancias" element={<PermissionGuard allOf={["diagnostics.work"]}><ConsultantEarnings /></PermissionGuard>} />
                  <Route path="consultas-directas" element={<PermissionGuard anyOf={["consultant.quick_consultation.provide", "consultant.talk.provide"]}><ConsultantServiceWork /></PermissionGuard>} />
                  <Route path="oraculo-consultor" element={<PermissionGuard allOf={["consultant.oracle.provide"]}><OracleConsultant /></PermissionGuard>} />

                  {/* Creador */}
                  <Route path="mis-productos" element={<PermissionGuard allOf={["marketplace.products.view_own"]}><CreatorProducts /></PermissionGuard>} />
                  <Route path="subir-producto" element={<PermissionGuard allOf={["marketplace.products.create_draft"]}><CreatorProducts /></PermissionGuard>} />
                  <Route path="inventario" element={<PermissionGuard allOf={["marketplace.inventory.manage_own"]}><CreatorInventory /></PermissionGuard>} />
                  <Route path="pedidos-marketplace" element={<PermissionGuard allOf={["marketplace.orders.view_own"]}><CreatorMarketplaceOps /></PermissionGuard>} />
                  <Route path="ventas" element={<PermissionGuard allOf={["marketplace.earnings.view_own"]}><CreatorMarketplaceOps mode="earnings" /></PermissionGuard>} />
                  <Route path="ganancias" element={<PermissionGuard allOf={["marketplace.earnings.view_own"]}><CreatorMarketplaceOps mode="earnings" /></PermissionGuard>} />
                  <Route path="configuracion-tienda" element={<PermissionGuard allOf={["marketplace.store.manage_own"]}><CreatorStore /></PermissionGuard>} />
                  <Route path="contenidos-academia" element={<PermissionGuard allOf={["courses.view_own_drafts"]}><CreatorAcademy /></PermissionGuard>} />

                  {/* Admin */}
                  <Route path="usuarios" element={<PermissionGuard allOf={["users.view"]}><GestionUsuarios /></PermissionGuard>} />
                  <Route path="actividad-usuarios" element={<PermissionGuard allOf={["activity.overview"]}><ActivityCenter /></PermissionGuard>} />
                  <Route path="radar-retencion" element={<PermissionGuard allOf={["activity.overview"]}><RetentionRadar /></PermissionGuard>} />
                  <Route path="asignaciones" element={<PermissionGuard allOf={["assignments.manage"]}><Asignaciones /></PermissionGuard>} />
                  <Route path="revision-diagnosticos" element={<PermissionGuard allOf={["diagnostics.review"]}><AdminDiagnosticOperations /></PermissionGuard>} />
                  <Route path="entregas-finales" element={<PermissionGuard allOf={["diagnostics.delivery.approve"]}><RevisionDiagnosticos /></PermissionGuard>} />
                  <Route path="acompanamientos-admin" element={<PermissionGuard allOf={["assignments.manage"]}><Asignaciones /></PermissionGuard>} />
                  <Route path="tickets" element={<PermissionGuard allOf={["tickets.view"]}><SupportCenter staff /></PermissionGuard>} />
                  <Route path="reportes" element={<PermissionGuard allOf={["system.config.view"]}><ReportsAnalytics /></PermissionGuard>} />
                  <Route path="marketplace-admin" element={<PermissionGuard allOf={["marketplace.products.review"]}><MarketplaceModeration /></PermissionGuard>} />
                  <Route path="revision-contenidos-creador" element={<PermissionGuard allOf={["courses.review"]}><AcademyCreatorReview /></PermissionGuard>} />
                  <Route path="configuracion-operativa" element={<PermissionGuard allOf={["system.config.view"]}><GlobalConfig /></PermissionGuard>} />

                  {/* SuperAdmin */}
                  <Route path="pagos" element={<PermissionGuard anyOf={["payments.view_review_only", "payments.verify"]}><Pagos /></PermissionGuard>} />
                  <Route path="comprobantes" element={<Navigate to="/app/revision-diagnosticos" replace />} />
                  <Route path="pagos-diagnosticos" element={<Navigate to="/app/revision-diagnosticos" replace />} />
                  <Route path="premium" element={<PermissionGuard anyOf={["premium.activate", "premium.deactivate"]}><Premium /></PermissionGuard>} />
                  <Route path="super-diagnosticos" element={<PermissionGuard anyOf={["diagnostics.create", "diagnostics.edit"]}><DiagnosticsCatalog /></PermissionGuard>} />
                  <Route path="super-cursos" element={<PermissionGuard anyOf={["courses.create", "courses.edit"]}><AcademyCatalogAdmin /></PermissionGuard>} />
                  <Route path="super-marketplace" element={<PermissionGuard allOf={["marketplace.manage"]}><MarketplaceModeration /></PermissionGuard>} />
                  <Route path="super-roles" element={<PermissionGuard allOf={["roles.manage"]}><TrustedTeamManagement /></PermissionGuard>} />
                  <Route path="super-administradores" element={<Navigate to="/app/super-roles" replace />} />
                  <Route path="super-equipo" element={<Navigate to="/app/super-roles" replace />} />
                  <Route path="configuracion" element={<PermissionGuard allOf={["system.config"]}><GlobalConfig /></PermissionGuard>} />

                  {/* Soporte KD9 */}
                  <Route path="tickets-escalados" element={<PermissionGuard allOf={["tickets.view", "tickets.escalate"]}><SupportCenter staff presetStatus="escalated" /></PermissionGuard>} />
                  <Route path="tickets-cerrados" element={<PermissionGuard allOf={["tickets.view"]}><SupportCenter staff presetStatus="closed" /></PermissionGuard>} />
                  <Route path="usuarios-problemas" element={<PermissionGuard allOf={["tickets.view", "users.view"]}><SupportCenter staff problems /></PermissionGuard>} />
                  <Route path="diagnostico-sesiones" element={<PermissionGuard allOf={["activity.support"]}><SessionDiagnostics /></PermissionGuard>} />
                  <Route path="reportes-tecnicos" element={<PermissionGuard allOf={["tickets.view"]}><SupportCenter staff presetCategory="technical" /></PermissionGuard>} />
                  <Route path="escalar" element={<PermissionGuard allOf={["tickets.escalate"]}><SupportCenter staff /></PermissionGuard>} />

                  {/* Existing pages kept */}
                  <Route path="estudios" element={<PermissionGuard allOf={["diagnostics.buy"]}><Estudios /></PermissionGuard>} />
                  <Route path="academia" element={<PermissionGuard anyOf={["courses.buy", "academy.view"]}><Academia /></PermissionGuard>} />
                  <Route path="tienda" element={<PermissionGuard allOf={["marketplace.buy"]}><Tienda /></PermissionGuard>} />
                  <Route path="pedidos" element={<Navigate to="/app/mis-compras-marketplace" replace />} />
                  <Route path="mis-pedidos" element={<Navigate to="/app/mis-diagnosticos" replace />} />
                  <Route path="perfil" element={<PermissionGuard allOf={["profile.manage"]}><Perfil /></PermissionGuard>} />
                  <Route path="mensajes" element={<Mensajes />} />
                  <Route path="comunidad" element={<Navigate to="/app/inicio" replace />} />
                  <Route path="favoritos" element={<Navigate to="/app/tienda" replace />} />
                  <Route path="consultor" element={<PermissionGuard allOf={["diagnostics.work"]}><Consultor /></PermissionGuard>} />
                  <Route path="admin" element={<PermissionGuard allOf={["assignments.manage"]}><Admin /></PermissionGuard>} />
                  <Route path="creador" element={<PermissionGuard allOf={["marketplace.products.view_own"]}><Creador /></PermissionGuard>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            </div>
          </AuthProvider>
        </RoleProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
